import React, { useState } from "react";
import { sdh_refineInteractive, sdh_previewOptions, type RefineProposal, type RefineOption,
         type RefineTurn, type OptionImpact, type EnrichedRule } from "../api/sdhAPI";
import SampleRecordsPanel from "./SampleRecordsPanel";

/**
 * Rule Studio — the runtime SME feedback loop for ONE rule.
 * She types what she wants changed, the agent proposes a revision, she sees a before/after diff and the
 * agent's explanation. Nothing is saved until she clicks Save (persist=true), so she can keep re-steering
 * with different wording until the rule says what she means.
 */
const errText = (e: any, fallback: string) =>
  e?.response?.data?.detail || e?.response?.data?.message || e?.data?.detail || e?.message || fallback;

const FIELDS: Array<[string, string]> = [
  ["constraint_family", "Rule type (family)"],
  ["condition", "Condition (WHEN)"],
  ["predicate_template", "Predicate (THEN) — executable"],
  ["expected_outcome", "Expected outcome (THEN)"],
  ["business_logic", "Plain-English meaning"],
  ["severity", "Severity"],
];

const QUICK = [
  "Bind this to the governed Collibra values; obsolete codes are not allowed.",
  "Only applies to finished goods (MTART = FERT).",
  "This is not mandatory for all material types — narrow the scope.",
  "The rule flags too much — tighten it so it only catches real exceptions.",
];

export default function RuleStudioPanel(
  { entityId, appNm, rule, onClose, onSaved }:
  { entityId: string; appNm: string; rule: EnrichedRule; onClose: () => void; onSaved: (m: string) => void }
) {
  const stored = (rule as any).sme_decision?.comment || "";
  const [directive, setDirective] = useState<string>(stored);
  const [prop, setProp] = useState<RefineProposal | null>(null);
  const [opts, setOpts] = useState<RefineOption[]>([]);
  const [pick, setPick] = useState<string | null>(null);
  const [interp, setInterp] = useState<string | null>(null);
  const [challenge, setChallenge] = useState<string | null>(null);
  const [question, setQuestion] = useState<string | null>(null);
  const [before, setBefore] = useState<Record<string, any> | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [tries, setTries] = useState(0);
  // The agent is stateless, so the panel keeps the exchange and replays it on every ask. That is how a
  // follow-up answer ("FERT and HALB") is understood as the reply to the agent's last question.
  const [convo, setConvo] = useState<RefineTurn[]>([]);
  const [impact, setImpact] = useState<Record<string, OptionImpact>>({});
  const [impBusy, setImpBusy] = useState(false);
  const [samplesFor, setSamplesFor] = useState<string | null>(null);   // option label

  const ask = async () => {
    if (!directive.trim()) { setErr("Tell the agent what you want changed."); return; }
    setBusy(true); setErr(null);
    try {
      const res = await sdh_refineInteractive(entityId, appNm, rule.rule_id, directive.trim(), false, 2,
        undefined, convo);
      if (!res.proposal) {
        // Say WHY. "try rewording" is useless when the real cause is a rejected predicate.
        setErr(res.reason || (res.status === "no_refinement"
          ? "The agent returned no proposal for this rule. Try naming the fields and the condition explicitly."
          : res.status));
        setProp(null);
      }
      else {
        const list = res.options || res.proposal.options || [];
        setProp(res.proposal); setOpts(list); setBefore(res.before || null);
        setInterp(res.interpretation || res.proposal.interpretation || null);
        setChallenge(res.challenge || res.proposal.challenge || null);
        setQuestion(res.question_for_sme || res.proposal.question_for_sme || null);
        setPick((list.find((o) => o.recommended) || list[0])?.label ?? null);
        // record this turn so the next ask carries the thread
        const agentSaid = [res.interpretation || res.proposal.interpretation,
          list.length ? `Options: ${list.map((o) => o.label).join(" | ")}` : "",
          res.question_for_sme || res.proposal.question_for_sme || ""]
          .filter(Boolean).join(" ");
        setConvo((c) => [...c, { role: "sme", content: directive.trim() },
                                { role: "agent", content: agentSaid }]);
        setTries((t) => t + 1);
        // Run every option on sample data so she can compare impact before choosing.
        if (list.length) {
          setImpBusy(true); setImpact({});
          sdh_previewOptions(entityId, appNm, rule.rule_id, list)
            .then((r) => {
              const m: Record<string, OptionImpact> = {};
              (r.options || []).forEach((o) => { if (o.label) m[o.label] = o; });
              setImpact(m);
            })
            .catch(() => {})
            .finally(() => setImpBusy(false));
        }
      }
    } catch (e: any) { setErr(errText(e, "Refine failed — see the server log for details.")); }
    finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      // send the proposal back so Save APPLIES what she is looking at — no second agent call
      const res = await sdh_refineInteractive(entityId, appNm, rule.rule_id, directive.trim(), true, 2,
        pick || undefined, convo, prop || undefined);
      onSaved(`Saved as iteration 2 — ${rule.rule_name || rule.rule_id} is now v${res.version ?? "?"}.`);
    } catch (e: any) { setErr(errText(e, "Save failed — see the server log for details.")); }
    finally { setBusy(false); }
  };

  const sel: RefineOption | undefined = opts.find((o) => o.label === pick) || opts[0];
  const rev: any = (sel?.revised as any) || prop?.revised || {};
  const act = sel?.action || prop?.action;
  const beforeOf = (k: string) => {
    const v = (before || {})[k];
    return v == null || v === "" ? "" : String(v);   // "" renders as "not set", never the enriched value
  };
  const changed = FIELDS.filter(([k]) => {
    const to = (rev as any)[k];
    return to != null && String(to).trim() !== "" && String(to) !== beforeOf(k);
  });

  return (
    <div className="rs-scrim" onClick={onClose}>
      <div className="rs" onClick={(e) => e.stopPropagation()}>
        <style>{CSS}</style>
        <header className="rs-h">
          <div>
            <div className="eyebrow">RULE STUDIO · agent feedback loop</div>
            <div className="title">{rule.rule_name || rule.rule_id}</div>
          </div>
          <button className="x" onClick={onClose}>×</button>
        </header>

        <div className="rs-body">
          <section>
            <div className="lbl">What should change?</div>
            <textarea className="rs-ta" rows={3} value={directive} placeholder="e.g. only applies to finished goods; bind to the governed values…"
              onChange={(e) => setDirective(e.target.value)} />
            <div className="quick">
              {QUICK.map((q) => (
                <button key={q} className="qchip" onClick={() => setDirective(q)}>{q.length > 46 ? q.slice(0, 46) + "…" : q}</button>
              ))}
            </div>
            {convo.length > 0 && (
              <details className="thread"><summary>{convo.length / 2} previous turn(s) in this conversation</summary>
                {convo.map((m, i) => (
                  <div key={i} className={"tm " + m.role}><b>{m.role === "sme" ? "You" : "Agent"}:</b> {m.content}</div>
                ))}
              </details>
            )}
            {stored && <div className="hint">Pre-filled from your review comment — edit it to steer the agent differently.</div>}
            <div className="acts">
              <button className="btn go" disabled={busy} onClick={ask}>
                {busy ? "Asking the agent…" : tries ? "Try again" : "Ask the agent"}
              </button>
              {tries > 0 && <span className="hint">attempt {tries} — nothing is saved yet</span>}
            </div>
            {err && <div className="rs-err">{err}</div>}
          </section>

          {prop && (
            <section className="rs-out">
              <div className="lbl">
                Agent proposal
                <span className={"act a-" + String(act || "").toLowerCase()}>{act}</span>
                {typeof sel?.confidence === "number" && <span className="conf">confidence {Math.round((sel.confidence as number) * 100)}%</span>}
              </div>
              {interp && <p className="interp"><b>What I think you mean:</b> {interp}</p>}
              {challenge && <div className="chal"><b>⚑ What the evidence shows</b><div>{challenge}</div></div>}
              {question && <div className="ask"><b>? </b>{question}<span className="hint2"> — answer it in the box above and ask again</span></div>}

              {opts.length > 1 && (
                <div className="opts">
                  <div className="opts-h">
                    <span>Step 1 · Choose an approach</span>
                    <em>{opts.length} options — impact is measured on your data</em>
                  </div>
                  {opts.map((o) => (
                    <label key={o.label} className={"opt" + (pick === o.label ? " on" : "")}>
                      <input type="radio" name="refopt" checked={pick === o.label} onChange={() => setPick(o.label)} />
                      <div className="ob">
                        <div className="ot">
                          {o.label}
                          <span className={"act a-" + String(o.action || "").toLowerCase()}>{o.action}</span>
                          {o.recommended && <span className="rec">recommended</span>}
                          {o.needs_input && <span className="ni">needs your input</span>}
                        </div>
                        {o.rationale && <div className="orat">{o.rationale}</div>}
                        {o.trade_off && <div className="otr"><b>Trade-off:</b> {o.trade_off}</div>}
                        {o.sme_action && <div className="osa"><b>You need to:</b> {o.sme_action}</div>}
                        {(() => {
                          const im = impact[o.label];
                          if (impBusy && !im) return <div className="oimp mut">measuring impact…</div>;
                          if (!im) return null;
                          if (im.status === "not_compilable")
                            return <div className="oimp bad">⚠ this option cannot be compiled — it would not run</div>;
                          if (im.violating == null) return null;
                          const fails = im.sample_failing || [];
                          const passes = im.sample_passing || [];
                          return (
                            <div className="oimp">
                              <div className="hd">
                                <b>On your data</b>
                                <span className={im.violating ? "bad" : "good"}>
                                  {im.violating.toLocaleString()} of {(im.applicable ?? 0).toLocaleString()} flagged
                                  {im.violation_rate_pct != null ? ` · ${im.violation_rate_pct}%` : ""}
                                </span>
                              </div>
                              {(fails.length > 0 || passes.length > 0) && (
                                <button className="srbtn"
                                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setSamplesFor(o.label); }}>
                                  ⌕ Inspect {fails.length} failing / {passes.length} passing record(s)
                                </button>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    </label>
                  ))}
                </div>
              )}
              {opts.length === 1 && sel?.rationale && <p className="why">{sel.rationale}</p>}
              {opts.length === 1 && sel?.trade_off && <p className="why tr"><b>Trade-off:</b> {sel.trade_off}</p>}

              {act === "DROP" ? (
                <div className="drop">The agent recommends dropping this rule.</div>
              ) : changed.length === 0 ? (
                <div className="drop">No material change proposed — try a more specific instruction.</div>
              ) : (
                /* two siblings must share a parent — a ternary branch takes ONE element */
                <>
                <div className="opts-h" style={{ marginTop: 14 }}>
                  <span>Step 2 · Review the change</span>
                  <em>{sel?.label ? `for “${sel.label}”` : ""}</em>
                </div>
                <div className="diffs">
                  {/* Execution comparison FIRST — the change that matters most is what it flags. */}
                  {(() => {
                    const cur = impact["Current rule (unchanged)"];
                    const nw = sel?.label ? impact[sel.label] : undefined;
                    if (!cur && !nw) return null;
                    const fmt = (m: any) => !m ? "—"
                      : m.status === "not_compilable" ? "cannot be executed"
                      : m.violating == null ? (m.status || "not measured")
                      : `${m.violating.toLocaleString()} of ${(m.applicable ?? 0).toLocaleString()} flagged` +
                        (m.violation_rate_pct != null ? ` · ${m.violation_rate_pct}%` : "");
                    const delta = (cur?.violating != null && nw?.violating != null)
                      ? nw.violating - cur.violating : null;
                    return (
                      <div className="diff exec">
                        <div className="dk">Impact on your data{delta != null && delta !== 0 &&
                          <em className={delta > 0 ? "up" : "down"}>
                            {delta > 0 ? `+${delta.toLocaleString()}` : delta.toLocaleString()} flagged
                          </em>}</div>
                        <div className="old"><span className="tag">before</span>{fmt(cur)}</div>
                        <div className="new"><span className="tag">after</span>{fmt(nw)}
                          {nw && ((nw.sample_failing || []).length || (nw.sample_passing || []).length) ? (
                            <button className="srbtn inline"
                              onClick={() => setSamplesFor(sel?.label || null)}>⌕ inspect records</button>
                          ) : null}
                        </div>
                      </div>
                    );
                  })()}
                  {changed.map(([k, label]) => (
                    <div className="diff" key={k}>
                      <div className="dk">{label}</div>
                      <div className="old"><span className="tag">before</span>{beforeOf(k) || "not set"}</div>
                      <div className="new"><span className="tag">after</span>{String((rev as any)[k])}</div>
                    </div>
                  ))}
                </div>
                </>
              )}

              <div className="acts end">
                <button className="btn ghost" onClick={() => { setProp(null); }}>Discard</button>
                <button className="btn save" disabled={busy} onClick={save}>Save as iteration 2</button>
              </div>
              <div className="hint">Saving bumps the rule version and marks it for your re-review with this diff attached.</div>
            </section>
          )}
        </div>
      </div>
      {samplesFor && (() => {
        const im = impact[samplesFor];
        if (!im) return null;
        const flds = ((rule as any)._raw?.participating_fields || [])
          .map((p: any) => p.field_id).filter(Boolean);
        return <SampleRecordsPanel
          title={`${samplesFor} — ${rule.rule_name || rule.rule_id}`}
          subtitle={im.violating != null
            ? `${im.violating.toLocaleString()} of ${(im.applicable ?? 0).toLocaleString()} flagged on your data`
            : undefined}
          fails={im.sample_failing} passes={im.sample_passing}
          ruleFields={flds} onClose={() => setSamplesFor(null)} />;
      })()}
    </div>
  );
}

const CSS = `
.oimp{font-size:12px;margin-top:7px;background:#f4f8fa;border:1px solid #dbe6ee;border-radius:8px;padding:7px 10px;color:#33506e;line-height:1.5}
.oimp .hd{display:flex;align-items:baseline;gap:8px;flex-wrap:wrap}
.oimp .hd .bad{color:#b23a30;font-weight:700;font-family:ui-monospace,monospace}
.oimp .hd .good{color:#2f7a4f;font-weight:700;font-family:ui-monospace,monospace}
.srbtn{all:unset;cursor:pointer;margin-top:6px;display:inline-flex;align-items:center;gap:5px;font-size:11.5px;
 color:#0e7c7b;border:1px solid #bfe0de;background:#f2f8f8;border-radius:20px;padding:3px 11px}
.srbtn:hover{background:#0e7c7b;color:#fff;border-color:#0e7c7b}
.srbtn.inline{margin:0 0 0 10px;padding:2px 9px;font-size:11px}
.diff.exec .dk{background:#eef4f8;color:#2c5b7a}
.diff.exec .dk em{font-style:normal;margin-left:9px;font-family:ui-monospace,monospace;font-size:11px}
.diff.exec .dk em.up{color:#b23a30}.diff.exec .dk em.down{color:#2f7a4f}
.diff.exec .old,.diff.exec .new{font-family:system-ui,sans-serif;font-size:12.5px}
.oimp .samp{margin-top:5px}
.oimp .samp summary{cursor:pointer;color:#0e7c7b;font-size:11.5px}
.oimp .row{font-family:ui-monospace,monospace;font-size:11px;padding:3px 7px;border-radius:5px;margin-top:3px;white-space:pre-wrap}
.oimp .row.fail{background:#fdf3f2;color:#8a3f38}
.oimp .row.pass{background:#f1faf5;color:#1d6b47}
.oimp em{font-style:normal;font-weight:700;color:#b23a30}
.oimp .ex{color:#6b7c8d}
.oimp.bad{background:#fdeceb;color:#b23a30}
.oimp.mut{background:#f6f8fa;color:#6b7c8d}
.chal{font-size:13px;color:#8a3f38;background:#fdf3f2;border:1px solid #f0cdc8;border-radius:9px;padding:10px 12px;margin:0 0 10px;line-height:1.55}
.chal b{display:block;margin-bottom:3px}
.opt .osa{font-size:12px;color:#0b5f5e;background:#eef7f6;border-radius:6px;padding:5px 8px;margin-top:5px;line-height:1.45}
.thread{margin-top:9px;font-size:12px;color:#5a6675}
.thread summary{cursor:pointer;color:#0e7c7b}
.thread .tm{padding:5px 9px;border-radius:7px;margin-top:5px;line-height:1.45}
.thread .tm.sme{background:#eef7f6}.thread .tm.agent{background:#f6f8fa}
.interp{font-size:13px;color:#0b5f5e;background:#eef7f6;border-radius:9px;padding:9px 12px;margin:0 0 10px;line-height:1.5}
.ask{font-size:12.5px;color:#8a6516;background:#fbf6ea;border:1px solid #ead9b4;border-radius:9px;padding:9px 12px;margin:0 0 12px}
.ask .hint2{color:#9a7b3a;font-style:italic}
.opts{display:flex;flex-direction:column;gap:8px;margin-bottom:12px}
.opts-h{display:flex;align-items:baseline;gap:9px;font-size:11px;letter-spacing:.07em;text-transform:uppercase;
 color:#5a6675;font-weight:700;padding-bottom:5px;border-bottom:1px solid #e3e8ee;margin-bottom:8px}
.opts-h em{font-style:normal;text-transform:none;letter-spacing:0;font-weight:400;color:#9aa6b2;font-size:11.5px}
.opt{display:flex;gap:10px;align-items:flex-start;border:1px solid var(--line);border-radius:10px;padding:10px 12px;cursor:pointer}
.opt:hover{background:#f8fafb}.opt.on{border-color:#0e7c7b;background:#f2f8f8}
.opt .ot{font-size:13.5px;font-weight:600;display:flex;align-items:center;gap:7px}
.opt .ni{font-size:9.5px;font-weight:700;letter-spacing:.05em;text-transform:uppercase;color:#8a6516;background:#f6eeda;border-radius:20px;padding:2px 7px}
.opt .rec{font-size:9.5px;font-weight:700;letter-spacing:.05em;text-transform:uppercase;color:#0e7c7b;background:#dff0ee;border-radius:20px;padding:2px 7px}
.opt .orat{font-size:12.5px;color:#33404e;margin-top:3px;line-height:1.5}
.opt .otr{font-size:12px;color:#8a6516;margin-top:4px;line-height:1.45}
.why.tr{background:#fbf6ea;color:#8a6516}
.rs-scrim{position:fixed;inset:0;z-index:88;background:rgba(14,36,54,.42);display:flex;align-items:center;justify-content:center;padding:24px}
.rs{--line:#e3e8ee;--muted:#6b7c8d;width:min(880px,96vw);max-height:92vh;background:#fff;border-radius:16px;display:flex;
 flex-direction:column;overflow:hidden;box-shadow:0 28px 70px rgba(0,0,0,.3);color:#0E2436}
.rs-h{display:flex;align-items:center;justify-content:space-between;padding:15px 20px;background:#0e2436;color:#fff}
.rs-h .eyebrow{font-size:10px;letter-spacing:.12em;color:#9db4c9}
.rs-h .title{font-size:16px;font-weight:700;font-family:'Space Grotesk',system-ui,sans-serif}
.rs-h .x{all:unset;cursor:pointer;font-size:24px;color:#cdd8ea}.rs-h .x:hover{color:#fff}
.rs-body{overflow:auto;padding:16px 20px 20px}
.rs-body section{padding-bottom:14px}
.lbl{display:flex;align-items:center;gap:9px;font-size:11px;letter-spacing:.08em;text-transform:uppercase;
 color:#9aa6b2;font-weight:700;margin-bottom:7px}
.rs-ta{width:100%;box-sizing:border-box;font:inherit;font-size:13.5px;padding:10px 12px;border:1px solid var(--line);
 border-radius:10px;resize:vertical}
.rs-ta:focus{outline:none;border-color:#0e7c7b;box-shadow:0 0 0 2px #d9ecea}
.quick{display:flex;gap:6px;flex-wrap:wrap;margin-top:8px}
.qchip{all:unset;cursor:pointer;font-size:11.5px;padding:4px 10px;border-radius:20px;background:#f1f4f7;color:#4a5763}
.qchip:hover{background:#e4ebf1}
.hint{font-size:11.5px;color:var(--muted);margin-top:7px}
.acts{display:flex;align-items:center;gap:10px;margin-top:12px}
.acts.end{justify-content:flex-end}
.btn{all:unset;cursor:pointer;padding:8px 16px;border-radius:9px;font-size:13px;font-weight:600}
.btn.go{background:#0e2436;color:#fff}.btn.save{background:#0e7c7b;color:#fff}
.btn.ghost{background:#eef1f5;color:#42586e}
.btn[disabled]{opacity:.5;cursor:not-allowed}
.rs-err{margin-top:10px;background:#fdeceb;color:#b23a30;border-radius:8px;padding:9px 12px;font-size:12.5px}
.rs-out{border-top:1px solid var(--line);padding-top:14px}
.act{font-size:10.5px;font-weight:700;border-radius:20px;padding:2px 9px;color:#fff}
.a-revise{background:#0e7c7b}.a-drop{background:#b23a30}.a-keep{background:#8a97a3}
.conf{font-size:11px;color:var(--muted);font-weight:400;text-transform:none;letter-spacing:0}
.why{font-size:13px;color:#33404e;line-height:1.55;background:#f6f8fa;border-radius:9px;padding:10px 12px;margin:0 0 12px}
.drop{font-size:13px;color:#8a6516;background:#fbf6ea;border:1px solid #ead9b4;border-radius:9px;padding:10px 12px}
.diffs{display:flex;flex-direction:column;gap:10px}
.diff{border:1px solid var(--line);border-radius:10px;overflow:hidden}
.dk{font-size:11px;font-weight:700;color:#42586e;background:#f4f7fa;padding:6px 11px;border-bottom:1px solid var(--line)}
.old,.new{font-size:12.5px;padding:9px 11px;font-family:ui-monospace,SFMono-Regular,monospace;line-height:1.5;white-space:pre-wrap;word-break:break-word}
.old{background:#fdf3f2;color:#8a3f38}.new{background:#f1faf5;color:#1d6b47;border-top:1px solid var(--line)}
.tag{display:inline-block;font-family:system-ui,sans-serif;font-size:9.5px;font-weight:700;letter-spacing:.06em;
 text-transform:uppercase;margin-right:8px;opacity:.65}
`;
